import { Component, output, signal, computed } from '@angular/core';
import { StoreService } from '../../../store/store.service';
import { LoyaltyService } from '../../services/loyalty.service';
import { ModalService } from '../../services/modal.service';
import { EMAIL_PATTERN_REGEX } from '../../configs/common.config';
import { TextboxComponent } from '../textbox/textbox.component';
import { ButtonComponent } from '../button/button.component';
import { ButtonGroupComponent, ButtonItem } from '../button-group/button-group.component';

@Component({
  selector: 'app-forgot-password',
  standalone: true,
  imports: [
    TextboxComponent,
    ButtonComponent,
    ButtonGroupComponent
  ],
  templateUrl: './forgot-password.component.html',
  styleUrl: './forgot-password.component.scss'
})
export class ForgotPasswordComponent {
  constructor(
    private readonly storeSvc: StoreService,
    private readonly loyaltySvc: LoyaltyService,
    private readonly modalSvc: ModalService
  ) {}

  // Signals
  public showModal = signal<boolean>(false);
  public email = signal<string>('');
  public isSubmitting = signal<boolean>(false);

  // Computed
  public localeObj = computed(() => this.storeSvc.localeObj());
  public propInfo = computed(() => this.storeSvc.propInfo());
  public isUppercaseLabels = computed(() => this.storeSvc.appStore.isUpperCaseLabel());

  // Outputs
  public handleBackToLogin = output<void>();
  public handleOpenSidePanel = output<string>();
  public handleRegisterNavigate = output<void>();

  // Button group configuration
  public buttonGroup = computed(() => [
    {
      id: 'back-to-login',
      label: this.localeObj().tf_1_Calendar_Loyalty_forgotPWD_backToLogin,
      variant: 'secondary',
      value: 'BACK_TO_LOGIN',
      isDisabled: this.isSubmitting()
    },
    {
      id: 'send',
      label: this.localeObj().tf_1_Calendar_Loyalty_forgotPWD_send,
      variant: 'primary',
      value: 'SEND',
      isDisabled: this.isSubmitting(),
      isLoading: this.isSubmitting()
    }
  ] as [ButtonItem, ...ButtonItem[]]);

  /**
   * Open the forgot password side panel
   */
  public openPanel(email?: string): void {
    this.showModal.set(true);
    if (email) {
      this.email.set(email);
    }
  }

  /**
   * Close the forgot password modal
   */
  public handleClose = (): void => {
    this.showModal.set(false);
    this.clearForm();
  };

  /**
   * Handle email input change
   */
  public handleEmailChange = (event: string): void => {
    this.email.set(event);
  };

  /**
   * Handle form submission
   */
  public handleSubmit = (): void => {
    const emailValue = this.email().trim();

    // Validate email
    if (!emailValue) {
      this.modalSvc.displayToastMessage({
        title: '',
        desc: this.localeObj().tf_1_Calendar_Loyalty_signIn_usernameOrPin,
        type: 'error'
      });
      return;
    }

    // Email regex validation using common config
    if (!EMAIL_PATTERN_REGEX.test(emailValue)) {
      this.modalSvc.displayToastMessage({
        title: '',
        desc: this.localeObj().tf_1_Calendar_Loyalty_signUp_emailInvalidError,
        type: 'error'
      });
      return;
    }

    this.isSubmitting.set(true);

    // Get property codes
    const propertyCodes = this.propInfo().propertyCode;

    // Call password reset API
    this.loyaltySvc.passwordReset(emailValue, propertyCodes).subscribe({
      next: (response) => {
        this.isSubmitting.set(false);

        if (response?.status?.status_code === 1000 && response?.status?.success) {
          // Show success toast
          this.modalSvc.displayToastMessage({
            title: '',
            desc: this.localeObj().tf_1_Calendar_Loyalty_forgotPWD_successMsg,
            type: 'success'
          });

          // Emit back to login after success
          setTimeout(() => {
            this.handleBackToLogin.emit();
          }, 500);
        } else {
          // Handle error codes
          const errorCode = response?.status?.status_code;
          const errorKey = `tf_1_Calendar_Loyalty_forgotPWD_errorCode_${errorCode}`;
          const errorMessage = this.localeObj()[errorKey] || response?.status?.status_text;

          this.modalSvc.displayToastMessage({
            title: '',
            desc: errorMessage,
            type: 'error'
          });
        }
      },
      error: (error) => {
        this.isSubmitting.set(false);
        this.modalSvc.displayToastMessage({
          title: '',
          desc: this.localeObj().tf_1_Calendar_Loyalty_errorMessage,
          type: 'error'
        });
        console.error('Password reset error:', error);
      }
    });
  };

  /**
   * Handle button group click
   */
  public handleButtonClick = (value: string): void => {
    if (value === 'BACK_TO_LOGIN') {
      this.handleBackToLogin.emit();
    } else if (value === 'SEND') {
      this.handleSubmit();
    }
  };

  /**
   * Handle register now click - opens the sign-up panel
   */
  public handleRegisterClick = (): void => {
    // navigate to sign-up page (for sign-in page context)
    this.handleRegisterNavigate.emit();

    // To open sign-up side panel (for header side panel context)
    this.handleOpenSidePanel.emit('LOYALTY_SIGN_UP');
  };

  /**
   * Clear form
   */
  private clearForm(): void {
    this.email.set('');
  }
}

